import { Language } from "@/types";
import { LANGUAGE_CONFIG } from "@/constants";

/**
 * Detects the user's preferred language from browser settings
 * with comprehensive fallback logic and edge case handling
 */
export function detectBrowserLanguage(): Language {
  // Check if we're in a browser environment
  if (typeof navigator === "undefined") {
    return LANGUAGE_CONFIG.DEFAULT_LANGUAGE;
  }

  // Get all browser language preferences (ordered by preference)
  const browserLanguages = [
    navigator.language, // Primary language
    ...(navigator.languages || []), // All preferred languages
  ].filter(Boolean);

  const supportedCodes = LANGUAGE_CONFIG.SUPPORTED_LANGUAGES.map(
    (lang) => lang.code
  );

  // Try to find exact or partial matches
  for (const browserLang of browserLanguages) {
    const normalizedLang = normalizeBrowserLanguage(browserLang);

    // Check for exact match first (e.g., "fr" === "fr")
    if (supportedCodes.includes(normalizedLang as Language)) {
      return normalizedLang as Language;
    }

    // Check for partial match (e.g., "en-US" → "en")
    const langCode = browserLang.split("-")[0].toLowerCase();
    if (supportedCodes.includes(langCode as Language)) {
      return langCode as Language;
    }
  }

  // No matches found, return default
  return LANGUAGE_CONFIG.DEFAULT_LANGUAGE;
}

/**
 * Normalizes browser language code to lowercase and handles common variants
 */
function normalizeBrowserLanguage(langCode: string): string {
  if (!langCode) return "en";

  const normalized = langCode.toLowerCase().trim();

  // Handle common language code variations
  const languageMap: Record<string, string> = {
    // English variants
    "en-us": "en",
    "en-gb": "en",
    "en-ca": "en",
    "en-au": "en",

    // French variants
    "fr-fr": "fr",
    "fr-ca": "fr",
    "fr-be": "fr",

    // German variants
    "de-de": "de",
    "de-at": "de",
    "de-ch": "de",

    // Italian variants
    "it-it": "it",
    "it-ch": "it",

    // Spanish variants
    "es-es": "es",
    "es-mx": "es",
    "es-ar": "es",
    "es-co": "es",
    "es-pe": "es",
    "es-ve": "es",
    "es-cl": "es",
    "es-ec": "es",
    "es-uy": "es",
    "es-py": "es",
    "es-bo": "es",
    "es-sv": "es",
    "es-hn": "es",
    "es-ni": "es",
    "es-cr": "es",
    "es-pa": "es",
    "es-gt": "es",
    "es-cu": "es",
    "es-do": "es",
  };

  return languageMap[normalized] || normalized.split("-")[0];
}

/**
 * Gets the initial language for the app, considering:
 * 1. Existing localStorage preference (user has chosen before)
 * 2. Browser language detection (first visit)
 * 3. Default fallback
 */
export function getInitialLanguage(): Language {
  // Check if user has a stored preference (from previous visits)
  if (typeof window !== "undefined") {
    const storedLang =
      localStorage.getItem("i18nextLng") ||
      sessionStorage.getItem("i18nextLng");

    if (
      storedLang &&
      LANGUAGE_CONFIG.SUPPORTED_LANGUAGES.some(
        (lang) => lang.code === storedLang
      )
    ) {
      return storedLang as Language;
    }
  }

  // First visit or no stored preference - detect from browser
  return detectBrowserLanguage();
}

/**
 * Validates if a language code is supported by the application
 */
export function isSupportedLanguage(langCode: string): langCode is Language {
  return LANGUAGE_CONFIG.SUPPORTED_LANGUAGES.some(
    (lang) => lang.code === langCode
  );
}

/**
 * Extracts and validates language from URL search parameters
 * Used for handling language switching via URL parameters like ?lang=fr
 */
export function getLanguageFromURL(): Language | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get("lang");

    if (langParam && isSupportedLanguage(langParam)) {
      return langParam;
    }
  } catch (error) {
    console.warn("Error parsing URL parameters for language:", error);
  }

  return null;
}

/**
 * Gets the initial language for the app, considering:
 * 1. URL parameter (highest priority for external redirects)
 * 2. Existing localStorage preference (user has chosen before)
 * 3. Browser language detection (first visit)
 * 4. Default fallback
 */
export function getInitialLanguageWithURL(): Language {
  // First check URL parameter (for external redirects)
  const urlLang = getLanguageFromURL();
  if (urlLang) {
    // Store the URL language preference for future visits
    if (typeof window !== "undefined") {
      localStorage.setItem("i18nextLng", urlLang);
    }
    return urlLang;
  }

  // Fallback to existing logic
  return getInitialLanguage();
}
